/**
 * `?returnTo=` plumbing for the "add funds, then get back to what you
 * were doing" loop (Hunter, launch-gap 09). Checkout / day-view /
 * school surfaces link to the billing pages with their current
 * location as `returnTo`; the billing pages render a "Back to your
 * order" CTA navigating there once funds are in.
 *
 * SECURITY: `safeReturnTo` is the single gate every read of the param
 * goes through. Only relative in-app paths survive — anything that
 * could leave the app (absolute URLs, scheme-relative `//host`,
 * backslash tricks the URL parser normalizes to `//`, embedded
 * schemes) resolves to `null` and the caller falls back to its
 * default destination. Pure string logic, no React — unit-tested in
 * `client/test/lib/return-to.test.ts`.
 */

/** Query param name shared by every surface that participates in the loop. */
export const RETURN_TO_PARAM = 'returnTo'

/**
 * Validate a raw `?returnTo=` value down to a safe in-app path.
 *
 * Accepts only strings that:
 *   - start with a single `/` (so `https://…`, `javascript:…`, and
 *     bare `foo` are all out),
 *   - do NOT start with `//` (scheme-relative redirect),
 *   - contain no `\` anywhere (browsers normalize `/\evil.com` to
 *     `//evil.com`),
 *   - contain no `://` anywhere (defense in depth against weird
 *     encodings that smuggle a scheme past the prefix check).
 *
 * Everything else → `null`; callers use their default destination.
 */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) {
    return null
  }
  if (!raw.startsWith('/') || raw.startsWith('//')) {
    return null
  }
  if (raw.includes('\\') || raw.includes('://')) {
    return null
  }
  return raw
}

/**
 * Append `?returnTo=<path>` to an in-app href (`&` when the base
 * already carries a query). The value is encoded, so paths with their
 * own query strings round-trip intact.
 */
export function withReturnTo(basePath: string, returnToPath: string): string {
  const sep = basePath.includes('?') ? '&' : '?'
  return `${basePath}${sep}${RETURN_TO_PARAM}=${encodeURIComponent(returnToPath)}`
}
